import { CheckCircle, Loader2, Clock, Camera, Star } from 'lucide-react';

interface Visit {
  visit_id: string;
  store_id: string;
  store_name: string;
  status: string;
  perfect_store_score: number | null;
  check_in_at: string;
  check_out_at: string | null;
  photo_count: number;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '--:--';
  }
}

function durationMinutes(checkIn: string, checkOut: string | null): number | null {
  if (!checkOut) return null;
  const diff = new Date(checkOut).getTime() - new Date(checkIn).getTime();
  return Math.round(diff / 60000);
}

export default function VisitTimeline({ visits }: { visits: Visit[] }) {
  if (visits.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-6 text-center">
        <Clock size={28} className="mx-auto text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">Nenhuma visita hoje</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {visits.map((visit, idx) => {
        const isCompleted = visit.status === 'COMPLETED' || visit.status === 'completed';
        const isInProgress = visit.status === 'IN_PROGRESS' || visit.status === 'in_progress';
        const duration = durationMinutes(visit.check_in_at, visit.check_out_at);
        const isLast = idx === visits.length - 1;

        return (
          <div key={visit.visit_id} className="flex gap-3">
            {/* Timeline line + dot */}
            <div className="flex flex-col items-center">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                  isCompleted
                    ? 'bg-green-100 text-green-600'
                    : isInProgress
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-400'
                }`}
              >
                {isCompleted ? (
                  <CheckCircle size={14} />
                ) : isInProgress ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Clock size={14} />
                )}
              </div>
              {!isLast && (
                <div className="w-px flex-1 bg-gray-200 my-1" />
              )}
            </div>

            {/* Content */}
            <div className={`flex-1 pb-4 ${isLast ? '' : ''}`}>
              <div className="bg-white rounded-xl border border-gray-100 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900 truncate pr-2">
                    {visit.store_name}
                  </span>
                  <span className="text-xs text-gray-400 shrink-0">
                    {formatTime(visit.check_in_at)}
                  </span>
                </div>

                <div className="flex items-center gap-3 text-xs text-gray-500">
                  {duration !== null && (
                    <span className="flex items-center gap-0.5">
                      <Clock size={11} /> {duration}min
                    </span>
                  )}
                  {isInProgress && (
                    <span className="text-blue-600 font-medium">Em andamento</span>
                  )}
                  <span className="flex items-center gap-0.5">
                    <Camera size={11} /> {visit.photo_count}
                  </span>
                  {isCompleted && visit.perfect_store_score !== null && (
                    <span className="flex items-center gap-0.5">
                      <Star size={11} className="text-amber-500" /> {Number(visit.perfect_store_score).toFixed(0)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
